import React from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import isAfterDay from 'react-dates/lib/utils/isAfterDay';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import omit from 'lodash/omit';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import { withStyles } from '@material-ui/core/styles';
import Calendar from 'icons/calendar';
import Tilde from 'icons/tilde';

import {
  secondary300,
} from '../../../src/themes/colors';


export const START_DATE = 'startDate';
export const END_DATE = 'endDate';
export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';
export const ANCHOR_RIGHT = 'right';
export const NAV_POSITION_TOP = 'navPositionTop';

const styles = theme => ({
  leftIconRoot: {
    fill: secondary300,
  },
  rightIconRoot: {
    fill: secondary300,
    position: 'absolute',
    right: '1.25rem',
    top: '1.25rem',

  },
  dateRangePickerWrapper: {
    '& .DateRangePicker': {
      display: 'block',
    },
    '& .DateRangePickerInput': {
      width: '100%',
    },
    '& .DateInput': {
      width: '5rem !important',
      lineHeight: '3rem',
    },
    '& .DateInput_input': {
      color: `${theme.palette.secondary.text} !important`,
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.875rem !important',
      fontWeight: '400 !important',
      lineHeight: '1.25rem !important',
      padding: '0.688rem 0 0.563rem !important',
      '&::placeholder': {
        paddingLeft: '0.25rem',
      },
    },
    '& .DateRangePickerInput_calendarIcon': {
      margin: '0.125rem -0.25rem 0 0.5rem',
    },
    '& .DateRangePickerInput__withBorder': {
      borderRadius: '0.375rem !important',
      overflow: 'hidden',
      border: `0.063rem solid ${theme.palette.secondary['300']}`,
      height: '3rem',
      '&:hover, &:focus, &:focus-within, &:active': {
        border: `0.063rem solid ${theme.palette.primary.main} !important`,
      },
    },
    '& .DateInput_input__focused': {
      borderBottom: 'none !important',
    },
    '& .DateRangePickerInput_clearDates': {
      padding: '0 !important',
      margin: '0 1.063rem 0 0.313rem !important',
      top: '0% !important',
      transform: 'translateY(0%) !important',
    },
    '& .DateRangePickerInput_clearDates_svg': {
      fill: theme.palette.gray.main,
      height: '3rem !important',
      width: '0.625rem !important',
    },
    '& .DateRangePickerInput_clearDates_default': {
      '&:hover, &:focus': {
        background: `${theme.palette.white} !important`,
      },
    },
    '& .CalendarMonth_table': {
      borderCollapse: 'inherit',
    },
    '& .CalendarMonth_caption': {
      color: `${theme.palette.secondary['1000']} !important`,
      fontSize: '0.875rem !important',
      paddingBottom: '3.438rem !important',
      fontWeight: '400',
      lineHeight: '1.25rem',
    },
    '& .DayPickerNavigation_button__default': {
      border: 'none !important',
      color: `${theme.palette.secondary['300']} !important`,
    },
    '& .DayPickerNavigation_button': {
      padding: '0 1.25rem !important',
      paddingTop: '1.25rem !important',
      border: 'none !important',
    },
    '& .DayPicker_weekHeader_li': {
      color: theme.palette.gray.contrastText,
      fontSize: '0.75rem',
      fontWeight: '400',
      lineHeight: '1rem',
    },
    '& .DayPicker_weekHeader': {
      top: '3.75rem !important',
      paddingTop: '0.625rem !important',
      borderTop: `0.063rem solid ${theme.palette.secondary['100']} !important`,
    },
    '& .CalendarDay__default': {
      color: `${theme.palette.secondary.text} !important`,
      border: `0.063rem solid ${theme.palette.transparent} !important`,
      background: `${theme.palette.white} !important`,
      fontSize: '0.875rem',
      borderRadius: '0.375rem',
      '&:hover': {
        border: `0.063rem solid ${theme.palette.secondary['300']} !important`,
      },
    },
    '& .CalendarDay__selected, .CalendarDay__selected:active, .CalendarDay__selected:hover': {
      background: `${theme.palette.primary.main} !important`,
      color: `${theme.palette.white} !important`,
      borderRadius: '0.375rem',
    },
    '& .CalendarDay__selected_span': {
      background: `${theme.palette.primary['100']} !important`,
      border: `0.063rem double ${theme.palette.transparent} !important`,
      borderRadius: '0 !important',
    },
    '& .CalendarDay__blocked_out_of_range': {
      color: `${theme.palette.gray.contrastText} !important`,
      '&:active, &:hover': {
        color: `${theme.palette.gray.contrastText} !important`,
      },
    },
    '& .DateRangePicker_picker': {
      borderRadius: '0.5rem !important',
      boxShadow: `${theme.shadows[3]} !important`,
    },
    '& .DayPicker__withBorder': {
      boxShadow: 'none !important',
      borderRadius: '0.5rem !important',
      backgroundColor: `${theme.palette.transparent} !important`,
    },
  },
});

class DateRangePickerWrapper extends React.Component {
  static propTypes = {
    // example props for the demo
    autoFocus: PropTypes.bool,
    autoFocusEndDate: PropTypes.bool,
    stateDateWrapper: PropTypes.func,
    initialStartDate: PropTypes.shape({}),
    initialEndDate: PropTypes.shape({}),
    startDateId: PropTypes.string,
    endDateId: PropTypes.string,
    startDatePlaceholderText: PropTypes.string,
    endDatePlaceholderText: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    screenReaderInputMessage: PropTypes.string,
    showClearDates: PropTypes.bool,
    showDefaultInputIcon: PropTypes.bool,
    customInputIcon: PropTypes.bool,
    customArrowIcon: PropTypes.bool,
    customCloseIcon: PropTypes.bool,
    block: PropTypes.bool,
    small: PropTypes.bool,
    regular: PropTypes.bool,
    renderMonthText: PropTypes.string,
    orientation: PropTypes.string,
    anchorDirection: PropTypes.string,
    horizontalMargin: PropTypes.string,
    withPortal: PropTypes.bool,
    withFullScreenPortal: PropTypes.bool,
    initialVisibleMonth: PropTypes.string,
    numberOfMonths: PropTypes.number,
    keepOpenOnDateSelect: PropTypes.bool,
    reopenPickerOnClearDates: PropTypes.bool,
    isRTL: PropTypes.bool,
    navPosition: PropTypes.string,
    navPrev: PropTypes.objectOf,
    navNext: PropTypes.objectOf,
    onPrevMonthClick: PropTypes.func,
    onNextMonthClick: PropTypes.func,
    onClose: PropTypes.func,
    renderCalendarDay: PropTypes.string,
    renderDayContents: PropTypes.string,
    minimumNights: PropTypes.number,
    enableOutsideDays: PropTypes.bool,
    isDayBlocked: PropTypes.bool,
    isDayHighlighted: PropTypes.bool,
    displayFormat: PropTypes.func,
    monthFormat: PropTypes.string,
    isOutsideRange: PropTypes.func,
    focusOnMounted: PropTypes.bool,
  };

  static defaultProps = {
    // example props for the demo
    autoFocus: false,
    autoFocusEndDate: false,
    initialStartDate: null,
    initialEndDate: null,
    // input related props
    startDateId: START_DATE,
    startDatePlaceholderText: 'Start date',
    endDateId: END_DATE,
    endDatePlaceholderText: 'End date',
    disabled: false,
    required: false,
    screenReaderInputMessage: '',
    showClearDates: true,
    showDefaultInputIcon: true,
    customInputIcon: null,
    customArrowIcon: null,
    customCloseIcon: null,
    block: false,
    small: false,
    regular: false,
    // calendar presentation and interaction related props
    renderMonthText: null,
    orientation: HORIZONTAL_ORIENTATION,
    anchorDirection: ANCHOR_LEFT,
    horizontalMargin: 0,
    withPortal: false,
    withFullScreenPortal: false,
    initialVisibleMonth: null,
    numberOfMonths: 2,
    keepOpenOnDateSelect: false,
    reopenPickerOnClearDates: false,
    isRTL: false,
    // navigation related props
    navPosition: NAV_POSITION_TOP,
    navPrev: <ChevronLeftRoundedIcon />,
    navNext: <ChevronRightRoundedIcon />,
    onPrevMonthClick() {},
    onNextMonthClick() {},
    onClose() {},
    // day presentation and interaction related props
    renderCalendarDay: undefined,
    renderDayContents: null,
    minimumNights: 1,
    enableOutsideDays: false,
    isDayBlocked: () => false,
    isDayHighlighted: () => false,
    // internationalization
    displayFormat: () => moment.localeData().longDateFormat('L'),
    monthFormat: 'MMMM YYYY',
    stateDateWrapper: date => date,
    isOutsideRange: day => isAfterDay(day, moment()),
    focusOnMounted: false,
  };

  constructor(props) {
    super(props);

    let focusedInput = null;
    if (props.autoFocus) {
      focusedInput = START_DATE;
    } else if (props.autoFocusEndDate) {
      focusedInput = END_DATE;
    }

    this.state = {
      focusedInput,
      startDate: props.initialStartDate,
      endDate: props.initialEndDate,
    };

    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
  }

  componentWillMount() {
    if (this.props.focusOnMounted) {
      this.setState({ focusedInput: START_DATE });
    }
  }

  onDatesChange({ startDate, endDate }) {
    const { stateDateWrapper, onChange } = this.props;
    onChange(startDate, endDate);
    this.setState({
      startDate: startDate && stateDateWrapper(startDate),
      endDate: endDate && stateDateWrapper(endDate),
    });
  }

  onFocusChange(focusedInput) {
    this.setState({ focusedInput });
  }

  render() {
    const { focusedInput, startDate, endDate } = this.state;
    const { classes } = this.props;

    // autoFocus, autoFocusEndDate, initialStartDate and initialEndDate are helper props for the
    // example wrapper but are not props on the SingleDatePicker itself and
    // thus, have to be omitted.
    const props = omit(this.props, [
      'autoFocus',
      'autoFocusEndDate',
      'initialStartDate',
      'initialEndDate',
      'stateDateWrapper',
    ]);

    return (
      <div className={classes.dateRangePickerWrapper}>
        <DateRangePicker
          {...props}
          onDatesChange={this.onDatesChange}
          onFocusChange={this.onFocusChange}
          focusedInput={focusedInput}
          startDate={startDate}
          endDate={endDate}
          hideKeyboardShortcutsPanel
          customInputIcon={<Calendar />}
          customArrowIcon={<Tilde />}
          navPrev={<ChevronLeftRoundedIcon classes={{ root: classes.leftIconRoot }} />}
          navNext={<ChevronRightRoundedIcon classes={{ root: classes.rightIconRoot }} />}
        />
      </div>
    );
  }
}

export default withStyles(styles, { withTheme: true })(DateRangePickerWrapper);
